import { merge } from 'lodash';
import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';

import { SnEvidenceUser } from '@lib/api/entities';
import { SnIntegrationsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { ISnAutocompleteFormFieldConfig } from '@lib/form/autocomplete';
import { ISnCheckboxFormFieldConfig } from '@lib/form/checkbox';
import { ISnFileUploadFormFieldConfigInterface } from '@lib/form/file-upload';
import { ISnInputFormFieldConfig } from '@lib/form/input';
import { ISnPlacesAutocompleteFormFieldConfig } from '@lib/form/places-autocomplete';
import { SnGoogleGeocoderResult } from '@lib/geo/google/geocoder';
import { ISnSelectSearchFormFieldConfig } from '@lib/select-search';
import { ISnSelectSearchDialogFormFieldConfig } from '@lib/select-search-dialog';
import { t } from '@lib/translation';

import { BriefcamCaseSearchDialogComponent } from '../components/briefcam-case-search-dialog/briefcam-case-search-dialog.component';
import { IncidentCaseSearchDialogComponent } from '../components/incident-case-search-dialog/incident-case-search-dialog.component';

@Injectable({ providedIn: 'root' })
export class SnIncidentFieldsGenerator {
  constructor(private readonly _integrationAPI: SnIntegrationsAPI) {}

  public getCaseNumberField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'caseNumber',
      type: 'input',
      templateOptions: {
        label: t('common.caseNumber'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getDescriptionField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'description',
      type: 'input',
      templateOptions: {
        label: t('common.description'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getAddressField(
    fieldConfig?: Partial<ISnAutocompleteFormFieldConfig<SnGoogleGeocoderResult>>,
  ): ISnAutocompleteFormFieldConfig<SnGoogleGeocoderResult> {
    const baseFieldConfig: ISnPlacesAutocompleteFormFieldConfig = {
      key: 'address',
      type: 'places-autocomplete',
      templateOptions: {
        label: t('common.address'),
        placeholder: t('common.enterAddress'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getCityField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'city',
      type: 'input',
      templateOptions: {
        label: t('common.city'),
        placeholder: t('incidentManagement.dialogs.createNewIncident.enterCity'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getStateField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'state',
      type: 'input',
      templateOptions: {
        label: t('common.state'),
        placeholder: t('incidentManagement.dialogs.createNewIncident.enterState'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getZipField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'zip',
      type: 'input',
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.zip'),
        placeholder: t('incidentManagement.dialogs.createNewIncident.enterZip'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getFileUploadField(
    fieldConfig?: Partial<ISnFileUploadFormFieldConfigInterface>,
  ): ISnFileUploadFormFieldConfigInterface {
    const baseFieldConfig: ISnFileUploadFormFieldConfigInterface = {
      key: 'files',
      type: 'file-upload',
      templateOptions: {
        multiple: true,
        allowPreview: true,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEvidenceTitleField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'evidenceTitle',
      type: 'input',
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.evidenceTitle'),
        placeholder: t('vault.incidents.fieldsGenerator.enterEvidenceTitle'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getBriefcamTitleField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'briefcamTitle',
      type: 'input',
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.briefcamTitle'),
        placeholder: t('vault.incidents.fieldsGenerator.enterBriefcamTitle'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEvidenceIdField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'evidenceId',
      type: 'input',
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.evidenceId'),
        placeholder: t('vault.incidents.fieldsGenerator.enterEvidenceId'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEvidenceDescriptionField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'evidenceDescription',
      type: 'input',
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.evidenceDescription'),
        placeholder: t('vault.incidents.fieldsGenerator.enterEvidenceDescription'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getBriefcamDescriptionField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'briefcamDescription',
      type: 'input',
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.briefcamDescription'),
        placeholder: t('vault.incidents.fieldsGenerator.enterBriefcamDescription'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEvidenceUserSelectField(
    fieldConfig?: Partial<ISnSelectSearchFormFieldConfig>,
    results?: any[],
  ): ISnSelectSearchFormFieldConfig {
    const options = results?.map((option: SnEvidenceUser) => {
      return {
        label: option.displayName,
        value: option.id,
      };
    });

    const baseFieldConfig: ISnSelectSearchFormFieldConfig = {
      key: 'userId',
      type: 'select-search',
      templateOptions: {
        label: t('common.user'),
        placeholder: t('vault.incidents.fieldsGenerator.selectUser'),
        options,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEvidenceCaseSelectField(
    fieldConfig?: Partial<ISnSelectSearchDialogFormFieldConfig>,
  ): ISnSelectSearchDialogFormFieldConfig {
    const baseFieldConfig: ISnSelectSearchDialogFormFieldConfig = {
      key: 'caseId',
      type: 'select-search-dialog',
      templateOptions: {
        searchPlaceholderLabel: t('vault.incidents.fieldsGenerator.selectAxonCase'),
        addComponent: IncidentCaseSearchDialogComponent,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getBriefcamCaseSelectField(fetchFn?: () => Observable<any>): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'caseId',
      type: 'http-select',
      templateOptions: {
        multiple: false,
        infinityScroll: true,
        valueProp: 'id',
        labelProp: 'name',
        label: t('vault.incidents.fieldsGenerator.case'),
        search: true,
        required: true,
        addComponent: BriefcamCaseSearchDialogComponent,
        fetchDataFn: fetchFn ?? (() => this._integrationAPI.getBriefcamCaseList()),
      },
    };

    return baseFieldConfig;
  }

  public getEvidenceTagSelectField(
    fieldConfig?: Partial<ISnSelectSearchFormFieldConfig>,
  ): ISnSelectSearchFormFieldConfig {
    const baseFieldConfig: ISnSelectSearchFormFieldConfig = {
      key: 'tags',
      type: 'select-search',
      templateOptions: {
        label: t('common.tags'),
        placeholder: t('vault.incidents.fieldsGenerator.selectTags'),
        multiple: true,
        selectAllOption: t('common.all'),
        addOptions: true,
        addButtonTooltip: t('vault.incidents.fieldsGenerator.addNewTag'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEvidenceCategorySelectField(
    fieldConfig?: Partial<ISnSelectSearchFormFieldConfig>,
  ): ISnSelectSearchFormFieldConfig {
    const baseFieldConfig: ISnSelectSearchFormFieldConfig = {
      key: 'categories',
      type: 'select-search',
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.categories'),
        placeholder: t('vault.incidents.fieldsGenerator.selectCategories'),
        multiple: true,
        selectAllOption: t('common.all'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEvidenceSyncField(
    fieldConfig?: Partial<ISnCheckboxFormFieldConfig>,
  ): ISnCheckboxFormFieldConfig {
    const baseFieldConfig: ISnCheckboxFormFieldConfig = {
      key: 'sync',
      type: 'checkbox',
      defaultValue: true,
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.syncContentsToAxonEvidence'),
        appearance: 'outline',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getEvidenceAutoSyncField(
    fieldConfig?: Partial<ISnCheckboxFormFieldConfig>,
  ): ISnCheckboxFormFieldConfig {
    const baseFieldConfig: ISnCheckboxFormFieldConfig = {
      key: 'autoSync',
      type: 'checkbox',
      defaultValue: true,
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.autoSyncNewFiles'),
        appearance: 'outline',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }
}
